import { Link } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  ChevronLeft,
  Info,
  LogIn,
  LogOut,
  Search,
  ClipboardList,
} from "lucide-react";
import { useCommunity } from "@/contexts/CommunityContext";
import { useState } from "react";

const directionLabels = {
  INBOUND: "Ingreso",
  OUTBOUND: "Egreso",
} as const;

const methodLabels = {
  QR_SCAN: "QR",
  MANUAL_ENTRY: "Manual",
  LPR: "LPR",
  RFID_TAG: "RFID",
} as const;

function formatDateTime(date: Date | string) {
  return new Date(date).toLocaleString("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

type AccessDirection = keyof typeof directionLabels;
type AccessMethod = keyof typeof methodLabels;

export function AccessLogPage() {
  const { selectedCommunityId } = useCommunity();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const { data: adminCommunities, isLoading: adminCommunitiesLoading } =
    trpc.amenity.getAdminCommunities.useQuery();

  const hasAdminAccess =
    selectedCommunityId &&
    adminCommunities?.some((c) => c.id === selectedCommunityId);

  const {
    data,
    isLoading: logsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpc.invitation.getAccessLogForCommunity.useInfiniteQuery(
    {
      communityId: selectedCommunityId || "",
      search: debouncedSearch || undefined,
      limit: 50,
    },
    {
      enabled: !!selectedCommunityId && !!hasAdminAccess,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    },
  );

  const logs = data?.pages.flatMap((p) => p.items) ?? [];

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearch(value);
    const timeout = setTimeout(() => setDebouncedSearch(value), 300);
    return () => clearTimeout(timeout);
  };

  if (adminCommunitiesLoading) {
    return (
      <div className="container mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!hasAdminAccess) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="mb-8">
          <Link
            to="/invitations/manage"
            className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Volver a gestionar invitaciones
          </Link>
          <h1 className="text-2xl font-bold md:text-3xl">Registro de acceso</h1>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No tenés permisos de administración para ver el registro de acceso.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-8">
        <Link
          to="/invitations/manage"
          className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Volver a gestionar invitaciones
        </Link>
        <h1 className="text-2xl font-bold md:text-3xl">Registro de acceso</h1>
        <p className="text-muted-foreground">
          Historial de ingresos y egresos del barrio.
        </p>
      </div>

      <div className="mb-6">
        <div className="relative w-full max-w-sm">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o DNI..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {logsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : logs.length > 0 ? (
        <>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {logs.map((log) => {
                  const unitDisplay = log.unit
                    ? log.unit.blockSection
                      ? `${log.unit.blockSection} - ${log.unit.unitNumber}`
                      : log.unit.unitNumber
                    : null;

                  return (
                    <div
                      key={log.id}
                      className="flex items-center justify-between p-4"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                          {log.direction === "INBOUND" ? (
                            <LogIn className="h-5 w-5 text-green-600" />
                          ) : (
                            <LogOut className="h-5 w-5 text-orange-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {log.guestName ?? "Invitado desconocido"}
                            </span>
                            <Badge
                              variant={
                                log.direction === "INBOUND"
                                  ? "default"
                                  : "secondary"
                              }
                              className="text-xs"
                            >
                              {
                                directionLabels[
                                  log.direction as AccessDirection
                                ]
                              }
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {methodLabels[log.method as AccessMethod]}
                            </Badge>
                          </div>
                          <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                            {unitDisplay && <span>{unitDisplay}</span>}
                            {unitDisplay && log.hostName && <span>•</span>}
                            {log.hostName && (
                              <span>Anfitrión: {log.hostName}</span>
                            )}
                            {log.guestDni && (
                              <>
                                <span>•</span>
                                <span>DNI: {log.guestDni}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        <div>{formatDateTime(log.timestamp)}</div>
                        <div className="text-xs">
                          Guardia: {log.guardName ?? "—"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Cargar más
              </Button>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">
              {debouncedSearch
                ? "No se encontraron registros con esa búsqueda."
                : "Todavía no hay registros de acceso."}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
